import React, { useMemo } from 'react';
import { Box } from '@apia/theme';
import { isEqual } from 'lodash-es';
import { useLatest, useMount } from 'ahooks';
import GetTextValue from './GetTextValue';
import { IApiaTree, TApiaTreePossibleValue } from '../../../../../types';
import { arrayOrArray } from '../../../../../utils/util';
import { useFieldContext } from '../../fieldsStore/FieldContext';
import MakeField from '../../fieldsStore/MakeField';
import { getFieldErrorStyles } from '../../style';
import { useApiaComponentContext } from '../../apiaComponent/ApiaComponentContext';
import useApiaFieldCommonProps from '../useApiaFieldCommonProps';
import usePropsSelector from '../../../../../store/propsStore/usePropsSelector';
import propsStore from '../../../../../store/propsStore/propsStore';
import { useApiaFormContext } from '../../page/ApiaForm';
import IProperty from '../../../../../JSApi/constants/IProperty';
import Tree from '../../../../common/generic/tree/Tree';
import TreeDataController from '../../../../common/generic/tree/TreeDataController';
import {
  TDataNode,
  TId,
  TNodesSelectionEvent,
  TTreeIconRenderer,
} from '../../../../common/generic/tree/types';
import makeIconFromUrl from '../../../../common/makeIconFromUrl';
import { makeApiaUrl } from '../../../../../api/apiaApi/apiaApi';
import { customEvents } from '../../../../../utils/dom';
import useTreeApi from './useTreeApi';
import { usePrevious } from '@apia/util';

type TAppend = {
  handler: TreeDataController<TApiaTreePossibleValue>;
  possibleValue: TApiaTreePossibleValue | TApiaTreePossibleValue[];
  parentId: TId;
  selectedIds: TId[];
  parentIcon?: string;
  leafIcon?: string;
};

function appendPossibleValue(props: TAppend) {
  arrayOrArray(props.possibleValue).forEach((definition) => {
    const isLeaf = arrayOrArray(definition.possibleValue).length === 0;
    let icon: TTreeIconRenderer<TApiaTreePossibleValue> | undefined;
    if (isLeaf && props.leafIcon) {
      const Icon = makeIconFromUrl({
        className: 'tree__custom__icon',
        href: makeApiaUrl({
          ajaxUrl: 'getImage.run',
          image: props.leafIcon,
        }),
      });
      icon = function TreeIcon() {
        return <Icon />;
      };
    }
    if (!isLeaf && props.parentIcon) {
      const Icon = makeIconFromUrl({
        className: 'tree__custom__icon',
        href: makeApiaUrl({
          ajaxUrl: 'getImage.run',
          image: props.parentIcon,
        }),
      });
      icon = function TreeIcon() {
        return <Icon />;
      };
    }

    const newLeaf: TDataNode<TApiaTreePossibleValue> = {
      children: [],
      id: definition.value,
      label: definition.text,
      nodeProps: definition,
      isExpanded: true,
      parentId: props.parentId,
      isSelected: props.selectedIds.includes(definition.value),
      isLeaf,
      icon,
    };
    props.handler.append(newLeaf);

    if (definition.possibleValue)
      appendPossibleValue({
        ...props,
        possibleValue: definition.possibleValue,
        parentId: definition.value,
      });
  });
}

function getSelectedIds(
  tree: TApiaTreePossibleValue | TApiaTreePossibleValue[],
): string[] {
  if (!tree) return [];

  const selectedIds: string[] = [];

  if (Array.isArray(tree)) {
    tree.forEach((leaf) => selectedIds.push(...getSelectedIds(leaf)));
  } else {
    if (tree.selected) selectedIds.push(tree.value);

    if (tree.possibleValue) {
      arrayOrArray(tree.possibleValue).forEach((possibleValue) => {
        selectedIds.push(...getSelectedIds(possibleValue));
      });
    }
  }

  return selectedIds;
}

const ApiaTree = MakeField(
  React.forwardRef(() => {
    const {
      doFocus,
      fireEvent,
      setValue,
      state: { isValid, value },
      markAsLoaded,
    } = useFieldContext<TId[]>();

    useMount(() => {
      doFocus(() => {
        const tree = ref.current?.querySelector('[role="tree"]') as HTMLElement;
        tree?.dispatchEvent(
          new CustomEvent(customEvents.focus, { bubbles: true }),
        );
        setTimeout(() => tree?.focus(), 0);
      });
    });

    const ref = React.useRef<HTMLElement | null>(null);

    const { fieldId } = useApiaComponentContext<IApiaTree>();
    const props = usePropsSelector(fieldId);
    const {
      multiselect: isMultiselect,
      selParent: shouldSelectParents,
      valueColor,
      visibilityHidden: isHidden,
      size,
      height,
      cssClass,
      field,
      parentIcon,
      leafIcon,
    } = props;
    const selectedIds = value;

    const [isFocused, setIsFocused] = React.useState(false);

    const {
      identifier: { className, ...identifier },
      isReadonly,
    } = useApiaFieldCommonProps();

    const { isFormReadOnly } = useApiaFormContext();

    const onSelect = React.useCallback(
      (selection: TNodesSelectionEvent<TApiaTreePossibleValue>) => {
        if (isReadonly || props.disabled) return;

        setValue(
          selection
            .filter((thisNode) => {
              const backPack = thisNode?.nodeProps;
              const isParent = !!backPack?.possibleValue;
              if (!shouldSelectParents && isParent) return false;

              return !!thisNode;
            })
            .map((thisNode) => {
              return thisNode.id;
            }),
        );

        fireEvent('onChange');
      },
      [fireEvent, isReadonly, setValue, shouldSelectParents, props.disabled],
    );

    const treeProps = useTreeApi();
    const treePropsRef = useLatest(treeProps);

    const handler =
      React.useRef<TreeDataController<TApiaTreePossibleValue> | null>(null);
    const getHandler = React.useCallback(
      (newHandler?: TreeDataController<TApiaTreePossibleValue>) => {
        markAsLoaded();
        if (newHandler) handler.current = newHandler;
        if (!handler.current) return;

        const { possibleValue } = field as IApiaTree;
        handler.current.batchInit();
        handler.current.removeAll();
        appendPossibleValue({
          handler: handler.current,
          parentId: 'root',
          possibleValue,
          selectedIds,
          leafIcon,
          parentIcon,
        });
        handler.current.batchFinish();
      },
      [field, leafIcon, markAsLoaded, parentIcon, selectedIds],
    );

    const previousPossibleValues = usePrevious(
      (props.field as IApiaTree).possibleValue,
    );
    if (
      !isEqual(
        previousPossibleValues.current,
        (props.field as IApiaTree).possibleValue,
      )
    ) {
      if (handler.current) {
        handler.current.batchInit();
        handler.current.removeAll();
        appendPossibleValue({
          handler: handler.current,
          parentId: 'root',
          possibleValue: (props.field as IApiaTree).possibleValue,
          selectedIds,
          leafIcon,
          parentIcon,
        });
        handler.current.batchFinish();
      }
    }

    React.useEffect(() => {
      handler.current?.setSelectedNodes(selectedIds, true);
    }, [selectedIds]);

    const readonlyValue = React.useMemo(
      () => (
        <GetTextValue
          value={arrayOrArray(value) as string[]}
          possibleValues={arrayOrArray((field as IApiaTree).possibleValue)}
        />
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const handleOnSelect = React.useCallback(
      (ev: TNodesSelectionEvent<TApiaTreePossibleValue>) => {
        onSelect(ev);
        treePropsRef.current?.onSelect?.(ev);
      },
      [onSelect, treePropsRef],
    );

    const handleGetHandler = React.useCallback(
      (newHandler: TreeDataController<TApiaTreePossibleValue>) => {
        getHandler(newHandler);
        treePropsRef.current?.getHandler?.(newHandler);
      },
      [getHandler, treePropsRef],
    );

    if (
      (isFormReadOnly || props[IProperty.PROPERTY_INPUT_AS_TEXT]) &&
      !propsStore.getFieldProps(fieldId).hasFinishedLoading
    )
      markAsLoaded();
    const treeBoxProps = useMemo(
      () => ({
        'aria-label': props[IProperty.PROPERTY_ARIA_LABEL],
        'aria-description': props[IProperty.PROPERTY_ARIA_DESCRIPTION],
        autoFocus: props[IProperty.PROPERTY_AUTO_FOCUS] === 'true',
        title: props[IProperty.PROPERTY_TITLE],
      }),
      [props],
    );

    return (
      <Box
        ref={ref}
        title={props[IProperty.TYPE_TITLE]}
        as="div"
        className={`tree ${isReadonly ? 'readonly' : ''} ${
          props.disabled ? 'disabled' : ''
        } ${cssClass ?? ''} ${isFocused ? 'focused' : ''} ${className ?? ''}`}
        {...window.getVariant('layout.execution.form.fields.tree')}
        sx={React.useMemo(
          () => ({
            visibility: isHidden ? 'hidden' : 'visible',
            width: size ? `${size}px` : undefined,
            height: height ? `${height}px` : undefined,
            overflow: height ? 'auto' : undefined,

            ...getFieldErrorStyles(isValid),
            ...(valueColor
              ? {
                  li: {
                    color: valueColor,
                  },
                }
              : undefined),
          }),
          [height, isHidden, isValid, size, valueColor],
        )}
        onFocus={React.useCallback(() => setIsFocused(true), [])}
        onBlur={React.useCallback(() => setIsFocused(false), [])}
        {...identifier}
      >
        {!(isFormReadOnly || props[IProperty.PROPERTY_INPUT_AS_TEXT]) ? (
          <Tree<TApiaTreePossibleValue>
            disableSelection={isReadonly || props.disabled}
            isMultiple={isMultiselect}
            name={fieldId}
            label={props.attLabel}
            toggleNodesOnLabelClick={false}
            onSelect={handleOnSelect}
            getHandler={handleGetHandler}
            treeBoxProps={treeBoxProps}
          />
        ) : (
          readonlyValue
        )}
      </Box>
    );
  }),
  {
    getInitialValue({ context: { fieldId } }) {
      const { possibleValue } = propsStore.getFieldProps(fieldId)
        .field as IApiaTree;
      return getSelectedIds(arrayOrArray(possibleValue));
    },
    preSynchronize({
      fieldContext: {
        isReadonly,
        state: { value, lastValidValue },
      },
    }) {
      if (isReadonly) return 'cancel';
      return !Array.isArray(value) ||
        !Array.isArray(lastValidValue) ||
        isEqual(
          [...(value as unknown[])].sort(),
          [...(lastValidValue as unknown[])].sort(),
        )
        ? 'cancel'
        : true;
    },
    doSynchronize({
      processFieldSubmit,
      fieldContext: {
        state: { value },
      },
    }) {
      return processFieldSubmit({ value, clearValues: true });
    },
    willMarkAsLoaded: true,
  },
);

export default ApiaTree;
